/**
 * WeMetRosterDrawer — the roster-scoped We Met flow (W2-3b, reworked in W2-3c
 * to the profile We-Met drawer's interaction model per Dexter's directive):
 * search the roster, tap people to select them, and one teal CTA submits the
 * whole selection at once.
 *
 * Opened by the recap `We met` CTAs (Home recap card + space-home ended cell)
 * for an ended event, listing everyone who was going.
 *
 * REUSE, not fork:
 * - Roster: `useEventAttendees` (the one hook for GET /events/{id}/participants,
 *   keyed by `eventAttendeesKey`), fetched lazily via `enabled: open`. Search is
 *   the endpoint's own server-side, case-insensitive `?search=` name filter,
 *   debounced by this caller exactly like `EventAttendeesTabFull`.
 * - Met state: `useWeMetConnectionWith` per row (chips + selectability), and the
 *   same `selectWeMetConnectionWith` derivation over one `useWeMetConnectionsList`
 *   read for the batch (count + create/confirm routing) — one selector, no drift.
 * - Batch `We met`: `useWeMetUserFanout` — the SAME mutation `WeMetSpacesDrawer`
 *   submits through, generalized so the counterpart user rides in the variables;
 *   fired once per selected `none`-state person with this event as the place.
 *   Its optimistic insert flips each row to "Pending" instantly.
 * - Selected `pending_incoming` people route to `useConfirmWeMetConnection`
 *   instead — a create would 409 (backend CASE 3).
 *
 * Post-submit mirrors `WeMetSpacesDrawer`: per-item toasts come from the
 * mutations; once the whole batch lands (409 counts as landed) the drawer
 * closes and the selection resets. A partial hard failure keeps the drawer open
 * with the failed people still selected for retry — never silent.
 *
 * iOS gotcha: a text input inside a vaul drawer triggers the "fly-to-top" bug.
 * Uses the keyboard sheet recipe. This drawer's device record (round 2 was
 * about outside-tap dismiss, not fly-to-top) is in the overlay design doc, §5.
 *
 * No liveness tick: the recap surfaces that open this drawer already decided
 * the phase, so there is no `useNow` here (leaf-tick isolation, §3).
 */
import { useRef, useState, type ReactNode } from 'react';
import { Check, Clock, Search, X } from 'lucide-react';
import { useDebounce } from 'use-debounce';
import { SearchEmptyState } from '@components/SearchEmptyState';
import { Button } from '@components/ui/button';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  KEYBOARD_SHEET_MAX_HEIGHT,
} from '@components/ui/drawer';
import { UserAvatar } from '@components/UserAvatar';
import { useEventAttendees } from '@hooks/useEventAttendees';
import { tryLoadAgain } from '@lib/tryLoadAgain';
import {
  selectWeMetConnectionWith,
  useConfirmWeMetConnection,
  useWeMetConnectionsList,
  useWeMetConnectionWith,
} from '@hooks/useWeMetConnectionsV2';
import { useWeMetUserFanout } from '@hooks/useWeMetFanout';
import { useFixedBodyLock } from '@hooks/useFixedBodyLock';
import { cn } from '@lib/utils';
import type { UserPublicRead } from '@models/api';
import type { WeMetUIConnectionState } from '@models/api/we-met-connection';
import { useAuth } from '@providers';
import { FOCUS_RING_CLASSES, PRESS_FEEDBACK_CLASSES } from './liveMotion';

const SKELETON_KEYS = ['s0', 's1', 's2', 's3'];

/** Inert state pill (Pending / Connected) — not interactive, no hit-area needs. */
const STATE_PILL =
  'flex h-9 shrink-0 items-center gap-1 rounded-full px-3 font-fira text-xs font-semibold text-mb-grey-450';

// Retry + Load more share the live layer's press/focus treatment (§4.1: press
// feedback everywhere; house focus ring incl. ring-offset-2) — W1-4 + W2-3b M2.
const LOAD_MORE = `mx-auto rounded-full px-4 py-3 font-fira text-sm font-semibold text-mb-teal disabled:opacity-60 ${PRESS_FEEDBACK_CLASSES} ${FOCUS_RING_CLASSES}`;
const RETRY = `rounded-full px-4 py-2 font-fira text-sm font-semibold text-mb-teal ${PRESS_FEEDBACK_CLASSES} ${FOCUS_RING_CLASSES}`;

/** Only these states can join the selection; the batch routes them apart on submit. */
const isSelectableState = (uiState: WeMetUIConnectionState) =>
  uiState === 'none' || uiState === 'pending_incoming';

const firstNameOf = (person: UserPublicRead) => person.full_name.split(' ')[0] || person.full_name;

interface SelectedPerson {
  userId: string;
  firstName: string;
}

/** In-flight batch bookkeeping; lives in a ref so settling doesn't re-render per item. */
interface BatchProgress {
  total: number;
  settled: number;
  failed: number;
}

export interface WeMetRosterDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The ended event whose going roster the drawer lists. */
  eventId: string;
  /** Event name — header copy + the We-Met place name on the wire. */
  eventName: string;
  /**
   * Fires on each mark that LANDS for this event — a created request, a
   * confirmed incoming one, or an idempotent 409 (the mark already exists).
   * May fire several times per batch; treat it idempotently. Never fires for
   * merely opening, selecting, or a hard failure. Recap surfaces wire their
   * hide-on-mark here (`useRecapMarkHidden`'s `onMarked`).
   */
  onMarked?: () => void;
}

export function WeMetRosterDrawer({
  open,
  onOpenChange,
  eventId,
  eventName,
  onMarked,
}: WeMetRosterDrawerProps) {
  const { user } = useAuth();
  // Only feeds the optimistic row's `user_id` and self-exclusion; the backend
  // derives the real identity from the JWT. Empty string excludes nobody.
  const currentUserId = user?.id ?? '';

  // Keyboard sheet recipe, part 2: body lock (see the file doc comment).
  useFixedBodyLock(open);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchInput, setSearchInput] = useState('');
  const [search] = useDebounce(searchInput.trim(), 300);

  // Raw taps, keyed by user id. Effective selection is DERIVED against the live
  // connection states, so people who stop being selectable (a create landed, a
  // state changed elsewhere) drop out without any pruning effect — and a person
  // filtered out of view by search stays selected.
  const [selected, setSelected] = useState<ReadonlyMap<string, SelectedPerson>>(() => new Map());
  // Count submitted while a batch is in flight, null when idle. Doubles as the
  // frozen footer-hint count: the optimistic flips drain the live selection
  // mid-flight, and "Tap who you met" under a "Sending" button reads as a glitch.
  const [submitting, setSubmitting] = useState<number | null>(null);
  const isSubmitting = submitting !== null;
  const batchRef = useRef<BatchProgress | null>(null);

  const {
    attendees,
    isLoading,
    isFetching,
    isError,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
  } = useEventAttendees(eventId, { search: search || undefined, enabled: open });

  // One list read powers the batch (count + routing); rows subscribe to the
  // same cache entry via useWeMetConnectionWith. Lazy like the roster.
  const { data: connections } = useWeMetConnectionsList({ enabled: open });

  const confirmConnection = useConfirmWeMetConnection();

  const resetLocalState = () => {
    setSelected(new Map());
    setSearchInput('');
  };

  /** Per-item settlement; closes the drawer only once the WHOLE batch landed. */
  const settleBatchItem = (failed: boolean) => {
    const batch = batchRef.current;
    if (!batch) return;
    batch.settled += 1;
    if (failed) batch.failed += 1;
    if (batch.settled < batch.total) return;

    batchRef.current = null;
    setSubmitting(null);
    if (batch.failed === 0) {
      // Mirror WeMetSpacesDrawer's post-submit: toast(s) already fired from the
      // mutations; close and reset. On failures we stay open — the failed rows
      // rolled back to selectable and remain selected for a retry.
      resetLocalState();
      onOpenChange(false);
    }
  };

  const { submitTo } = useWeMetUserFanout({
    currentUserId,
    onResult: outcome => {
      // A landed mark (201 or idempotent 409) is the hide-on-mark trigger —
      // reported BEFORE the batch tally so a mid-batch drawer dismissal
      // (which abandons the tally) can't swallow an already-landed mark.
      if (outcome === 'landed') onMarked?.();
      settleBatchItem(outcome === 'failed');
    },
  });

  const people = attendees.filter(person => person.id !== currentUserId);

  const effectiveSelection = [...selected.values()].filter(entry =>
    isSelectableState(selectWeMetConnectionWith(connections, entry.userId).uiState),
  );
  const selectedCount = effectiveSelection.length;

  const debouncePending = searchInput.trim() !== search;
  const searchActive = searchInput.trim().length > 0 || search.length > 0;
  // Mirrors EventAttendeesTabFull: show the box once there is a roster to search,
  // and keep it mounted while a query (or the post-clear debounce gap) is active.
  const showSearch = searchInput.length > 0 || people.length > 0 || debouncePending;
  // keepPreviousData freezes the prior rows while a new term loads; dim them so
  // the swap reads as a live filter rather than a glitch for a fast typist.
  const searching = !!search && isFetching && !isLoading;

  const showFooter =
    !isLoading && !(isError && people.length === 0) && (people.length > 0 || searchActive);

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      resetLocalState();
      // Belt-and-braces: a dismissal mid-batch abandons the tally so a reopen
      // starts clean instead of showing "Sending" until stragglers settle. Any
      // still-inflight mutations finish on their own (hook-level toasts/cache),
      // and their late settle callbacks hit the null guard above.
      batchRef.current = null;
      setSubmitting(null);
    }
    onOpenChange(isOpen);
  };

  const handleToggle = (person: UserPublicRead) => {
    if (isSubmitting) return;
    setSelected(prev => {
      const next = new Map(prev);
      if (next.has(person.id)) {
        next.delete(person.id);
      } else {
        next.set(person.id, { userId: person.id, firstName: firstNameOf(person) });
      }
      return next;
    });
  };

  const handleSubmit = () => {
    if (isSubmitting || effectiveSelection.length === 0) return;
    batchRef.current = { total: effectiveSelection.length, settled: 0, failed: 0 };
    setSubmitting(effectiveSelection.length);

    for (const entry of effectiveSelection) {
      const { connection, uiState } = selectWeMetConnectionWith(connections, entry.userId);
      if (uiState === 'pending_incoming' && connection) {
        // They already said you met — confirm the existing request (a create
        // would 409, backend CASE 3). Tally via mutateAsync's own promise:
        // TanStack v5 fires per-call mutate() callbacks only for the observer's
        // LATEST call (each mutate() overwrites mutateOptions and detaches the
        // observer from the prior mutation), so with ≥2 confirms in one batch
        // the earlier settlements would be lost and the tally would wedge.
        // mutateAsync returns THAT call's own execute() promise, which settles
        // per-mutation regardless of observer swaps; the hook-level toast /
        // cache / invalidation callbacks are untouched.
        void confirmConnection.mutateAsync(connection.id).then(
          () => {
            // Confirming an incoming request IS marking — same trigger as a create.
            onMarked?.();
            settleBatchItem(false);
          },
          () => {
            settleBatchItem(true);
          },
        );
      } else {
        submitTo({
          place: { kind: 'event', id: eventId, name: eventName },
          otherUserId: entry.userId,
          otherUserFirstName: entry.firstName,
        });
      }
    }
  };

  // While a batch is in flight, freeze the hint at the submitted count.
  const hintCount = submitting ?? selectedCount;
  const footerHint =
    hintCount === 0
      ? 'Tap who you met'
      : `${hintCount} ${hintCount === 1 ? 'person' : 'people'} selected`;

  let content: ReactNode;
  if (isLoading) {
    // Decorative placeholder — hidden from the a11y tree, so no list role.
    content = (
      <div className="flex flex-col gap-2" aria-hidden="true">
        {SKELETON_KEYS.map(key => (
          <RosterRowSkeleton key={key} />
        ))}
      </div>
    );
  } else if (isError && people.length === 0) {
    // Only blow the list away on a first-load failure; a failed "load more"
    // keeps the rows below (mirrors EventAttendeesTabFull).
    content = (
      <div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
        <p className="font-fira text-sm text-mb-grey-300">We couldn&apos;t load who was there.</p>
        <button
          type="button"
          onClick={() => {
            tryLoadAgain(refetch);
          }}
          className={RETRY}
        >
          Retry
        </button>
      </div>
    );
  } else if (people.length === 0 && search) {
    // Filtered-empty (distinct from roster-empty): the roster has people, the
    // query just matched no one.
    content = (
      <SearchEmptyState
        query={search}
        entityName="people"
        title="No one matches that"
        message={`No one there matches “${search}”`}
      />
    );
  } else if (people.length === 0) {
    content = (
      <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
        <h3 className="mb-1 font-fira text-sm font-medium text-mb-grey-400">Just you this time</h3>
        <p className="font-fira text-sm text-mb-grey-300">Nobody else was going to this one.</p>
      </div>
    );
  } else {
    content = (
      <>
        <ul
          role="list"
          aria-busy={searching}
          className={cn('flex flex-col gap-2', searching && 'opacity-60 transition-opacity')}
        >
          {people.map(person => (
            <WeMetRosterRow
              key={person.id}
              person={person}
              isSelected={selected.has(person.id)}
              onToggle={handleToggle}
              disabled={isSubmitting}
            />
          ))}
        </ul>
        {isFetchNextPageError || hasNextPage ? (
          <div className="flex">
            <button
              type="button"
              onClick={() => {
                // One button, two jobs: after a failed page it is a retry.
                if (isFetchNextPageError) tryLoadAgain(fetchNextPage);
                else void fetchNextPage();
              }}
              disabled={isFetchingNextPage}
              className={LOAD_MORE}
            >
              {isFetchingNextPage
                ? 'Loading more'
                : isFetchNextPageError
                  ? "Couldn't load more. Retry"
                  : 'Load more'}
            </button>
          </div>
        ) : null}
      </>
    );
  }

  return (
    // Device round 2 (outside-tap dismiss, cause unknown): overlay design doc §5.
    // Outside dismissal flows through
    // onOpenChange(false) → the controlling surface, so deferWhile release
    // (hide-on-mark) works identically to a button close.
    <Drawer open={open} onOpenChange={handleOpenChange}>
      {/* Keyboard sheet recipe, part 1: height cap. */}
      <DrawerContent className={cn('flex flex-col overflow-hidden', KEYBOARD_SHEET_MAX_HEIGHT)}>
        <DrawerHeader className="text-left">
          <DrawerTitle className="text-balance">Who did you meet at {eventName}?</DrawerTitle>
          <DrawerDescription>Mark the people you crossed paths with.</DrawerDescription>
          {showSearch ? (
            <div className="relative mt-2">
              <Search
                className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-600"
                aria-hidden="true"
              />
              <input
                ref={searchInputRef}
                type="text"
                value={searchInput}
                onChange={event => {
                  setSearchInput(event.target.value);
                }}
                placeholder="Search who was there"
                aria-label="Search who was there"
                className="h-10 w-full rounded-full border border-mb-beige bg-white pl-10 pr-10 text-base placeholder:text-gray-500 focus:border-mb-teal focus:outline-none focus:ring-1 focus:ring-mb-teal-ink"
              />
              {searchInput.length > 0 ? (
                // True 44px hit box (h-11/w-11) centered where the visual ✕
                // sits — the DISMISS_BUTTON_CLASSES sizing, anchored inside
                // the input's right edge.
                <button
                  type="button"
                  onClick={() => {
                    setSearchInput('');
                    searchInputRef.current?.focus();
                  }}
                  aria-label="Clear search"
                  className={`absolute right-0 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full text-gray-500 hover:text-gray-700 ${FOCUS_RING_CLASSES}`}
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </button>
              ) : null}
            </div>
          ) : null}
        </DrawerHeader>
        <div
          className={cn(
            'min-h-0 flex-1 overflow-y-auto px-4',
            showFooter ? 'pb-4' : 'pb-page-safe',
          )}
        >
          {content}
        </div>
        {showFooter ? (
          // p-0 + inner wrapper: DrawerFooter bakes in `p-4`, and tailwind-merge
          // doesn't know the custom `pb-page-safe` utility is a padding-bottom,
          // so stacking them would leave the safe-area inset to cascade order.
          <DrawerFooter className="p-0">
            <div className="flex flex-col gap-2 px-4 pt-3 pb-page-safe">
              <p className="mb-1 text-center font-fira text-xs text-gray-500">{footerHint}</p>
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={selectedCount === 0 || isSubmitting}
                className={`w-full rounded-xl bg-mb-teal text-mb-ink hover:bg-mb-teal-dark disabled:opacity-60 ${PRESS_FEEDBACK_CLASSES}`}
              >
                {isSubmitting ? (
                  <span className="inline-flex items-center gap-2">
                    <span
                      className="h-4 w-4 animate-spin rounded-full border-2 border-mb-ink border-t-transparent"
                      aria-hidden="true"
                    />
                    Sending
                  </span>
                ) : (
                  'We met'
                )}
              </Button>
            </div>
          </DrawerFooter>
        ) : null}
      </DrawerContent>
    </Drawer>
  );
}

interface WeMetRosterRowProps {
  person: UserPublicRead;
  isSelected: boolean;
  onToggle: (person: UserPublicRead) => void;
  disabled: boolean;
}

/**
 * One roster row. Selectable states (`none`, `pending_incoming`) render as a
 * whole-row toggle button (`aria-pressed`, the WeMetSpacesDrawer selection
 * visuals: teal ring + avatar check badge); `pending_incoming` adds the house
 * "Says you met" label (the Notifications vocabulary). Non-selectable states
 * keep their inert chips exactly as before: pending_outgoing → `Pending`,
 * confirmed → `Connected` (the OtherUserActionRow vocabulary).
 */
function WeMetRosterRow({ person, isSelected, onToggle, disabled }: WeMetRosterRowProps) {
  const { uiState } = useWeMetConnectionWith(person.id);
  const headline = person.professional_background ?? person.fields_of_expertise?.[0] ?? null;

  const identity = (
    <>
      <span className="relative shrink-0">
        <UserAvatar
          imageUrl={person.profile_picture}
          fullName={person.full_name}
          size="md"
          alt=""
          className="h-9 w-9"
        />
        {isSelected && isSelectableState(uiState) ? (
          <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-mb-teal">
            <Check className="h-2.5 w-2.5 text-mb-ink" strokeWidth={3} aria-hidden="true" />
          </span>
        ) : null}
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate font-fira text-sm font-medium text-gray-900">
          {person.full_name}
        </span>
        {headline ? (
          <span className="truncate font-fira text-xs text-mb-grey-300">{headline}</span>
        ) : null}
      </span>
    </>
  );

  if (!isSelectableState(uiState)) {
    return (
      <li className="flex min-h-14 items-center gap-2.5 rounded-xl bg-mb-offwhite px-3 py-1.5">
        {identity}
        {uiState === 'confirmed' ? (
          <span className={`${STATE_PILL} bg-mb-filter-selected`}>
            <Check className="h-3.5 w-3.5" aria-hidden="true" />
            Connected
          </span>
        ) : (
          <span className={`${STATE_PILL} bg-amber-100`}>
            <Clock className="h-3.5 w-3.5" aria-hidden="true" />
            Pending
          </span>
        )}
      </li>
    );
  }

  return (
    <li>
      <button
        type="button"
        onClick={() => {
          onToggle(person);
        }}
        disabled={disabled}
        aria-pressed={isSelected}
        aria-label={person.full_name}
        className={cn(
          'flex min-h-14 w-full items-center gap-2.5 rounded-xl px-3 py-1.5 text-left disabled:opacity-60',
          // ring-inset (device round 2): a plain ring draws OUTSIDE the box
          // and gets clipped at the scroll container's edges on device — the
          // selected card's teal border was cut off left+right. Inset keeps
          // the full 2px ring inside the row box regardless of any ancestor
          // overflow clip, with no spacing changes; the avatar check badge
          // still marks selection alongside it.
          isSelected ? 'bg-white ring-2 ring-inset ring-mb-teal' : 'bg-mb-offwhite',
          PRESS_FEEDBACK_CLASSES,
          FOCUS_RING_CLASSES,
        )}
      >
        {identity}
        {uiState === 'pending_incoming' ? (
          <span className="flex h-9 shrink-0 items-center rounded-full bg-mb-teal/10 px-3 font-fira text-xs font-semibold text-mb-teal-dark">
            Says you met
          </span>
        ) : null}
      </button>
    </li>
  );
}

const RosterRowSkeleton = () => (
  <div className="flex min-h-14 items-center gap-2.5 rounded-xl bg-mb-offwhite px-3 py-1.5">
    <div className="h-9 w-9 shrink-0 animate-pulse rounded-full bg-gray-200" />
    <div className="flex flex-1 flex-col gap-1.5">
      <div className="h-3.5 w-32 animate-pulse rounded bg-gray-200" />
      <div className="h-3 w-24 animate-pulse rounded bg-gray-100" />
    </div>
    <div className="h-9 w-20 shrink-0 animate-pulse rounded-full bg-gray-100" />
  </div>
);
